import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { uploadBlob, captureVideoPoster } from "../lib/media";
import AuthMedia from "./AuthMedia";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Plus, Upload, X, Loader2, Save, Trash2, Music4, Heart, Calendar, ArrowLeft, Mail, PenLine, Clock } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { id: null, title: "", content: "", media: [], song: null, release_type: "death", release_date: "", release_tz: "" };

// Fuso orario di default del browser (per un utente italiano: Europe/Rome)
const BROWSER_TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Rome"; }
  catch { return "Europe/Rome"; }
})();

// Elenco curato dei fusi orari più comuni (niente doppioni, tendina compatta).
// Il fuso del browser (per un italiano: Europe/Rome) viene messo in cima se non presente.
const CURATED_TIMEZONES = [
  "Europe/Rome", "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
  "Europe/Lisbon", "Europe/Athens", "Europe/Moscow", "Europe/Istanbul", "UTC",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Sao_Paulo", "America/Argentina/Buenos_Aires", "America/Mexico_City", "America/Toronto",
  "Africa/Casablanca", "Africa/Cairo", "Africa/Lagos", "Africa/Johannesburg", "Africa/Nairobi",
  "Asia/Jerusalem", "Asia/Dubai", "Asia/Karachi", "Asia/Kolkata", "Asia/Bangkok",
  "Asia/Shanghai", "Asia/Singapore", "Asia/Tokyo", "Asia/Seoul", "Asia/Hong_Kong",
  "Australia/Sydney", "Australia/Perth", "Pacific/Auckland", "Pacific/Honolulu",
];
const ALL_TIMEZONES = (() => {
  const base = [...CURATED_TIMEZONES];
  if (BROWSER_TZ && !base.includes(BROWSER_TZ)) base.unshift(BROWSER_TZ);
  return base;
})();

// Offset (in minuti, arrotondato) di un fuso IANA rispetto a UTC in un dato istante
const tzOffsetMinutes = (date, timeZone) => {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone, hour12: false, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const map = {};
  for (const p of dtf.formatToParts(date)) map[p.type] = p.value;
  const asUTC = Date.UTC(map.year, map.month - 1, map.day,
    map.hour === "24" ? 0 : map.hour, map.minute, map.second);
  return Math.round((asUTC - date.getTime()) / 60000);
};

// Etichetta tipo "UTC+02:00"
const tzOffsetLabel = (timeZone, date = new Date()) => {
  const off = tzOffsetMinutes(date, timeZone);
  const sign = off >= 0 ? "+" : "-";
  const abs = Math.abs(off);
  const hh = String(Math.floor(abs / 60)).padStart(2, "0");
  const mm = String(abs % 60).padStart(2, "0");
  return `UTC${sign}${hh}:${mm}`;
};

// Nome amichevole per i fusi più comuni
const TZ_FRIENDLY = {
  "Europe/Rome": "Ora italiana",
  "Europe/London": "Ora del Regno Unito",
  "Europe/Paris": "Ora dell'Europa centrale",
  "America/New_York": "Ora di New York",
  "America/Los_Angeles": "Ora di Los Angeles",
  "Asia/Tokyo": "Ora del Giappone",
  "Asia/Dubai": "Ora di Dubai",
  "Asia/Kolkata": "Ora dell'India",
  "UTC": "UTC",
};
const tzShortLabel = (timeZone) => TZ_FRIENDLY[timeZone] || (timeZone || "").split("/").pop().replace(/_/g, " ");

// ora scelta (wall clock "YYYY-MM-DDTHH:mm") nel fuso indicato -> ISO UTC per il backend
const wallToUtcIso = (val, timeZone) => {
  if (!val) return null;
  const [datePart, timePart] = val.split("T");
  if (!datePart || !timePart) return null;
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h, mi] = timePart.split(":").map(Number);
  const wallAsUTC = Date.UTC(y, mo - 1, d, h, mi, 0);
  let offset = tzOffsetMinutes(new Date(wallAsUTC), timeZone);
  let utc = wallAsUTC - offset * 60000;
  offset = tzOffsetMinutes(new Date(utc), timeZone); // rifinitura per bordi DST
  utc = wallAsUTC - offset * 60000;
  return new Date(utc).toISOString();
};

// ISO UTC (o legacy naive) -> wall clock "YYYY-MM-DDTHH:mm" nel fuso indicato
const utcIsoToWall = (iso, timeZone) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 16);
  const dtf = new Intl.DateTimeFormat("en-CA", {
    timeZone, hour12: false, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
  const map = {};
  for (const p of dtf.formatToParts(d)) map[p.type] = p.value;
  const hh = map.hour === "24" ? "00" : map.hour;
  return `${map.year}-${map.month}-${map.day}T${hh}:${map.minute}`;
};

const PersonMessages = ({ person, open, onOpenChange, onChanged, startNew = false }) => {
  const fileRef = useRef(null);
  const songRef = useRef(null);
  const [view, setView] = useState("list"); // list | edit
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingSong, setUploadingSong] = useState(false);

  const load = () => {
    if (!person?.heir_id) return;
    api.get(`/memories?heir_id=${person.heir_id}`).then((res) => setItems(res.data)).catch(() => setItems([]));
  };
  useEffect(() => {
    if (open) {
      load();
      if (startNew) { setForm({ ...emptyForm, release_tz: BROWSER_TZ }); setView("edit"); }
      else { setView("list"); }
    } /* eslint-disable-next-line */
  }, [open, person?.heir_id, startNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm({ ...emptyForm, release_tz: BROWSER_TZ }); setView("edit"); };
  const openEdit = (m) => {
    const tz = m.release_tz || BROWSER_TZ;
    setForm({ id: m.id, title: m.title || "", content: m.content || "", media: m.media || [], song: m.song || null,
      release_type: m.release_type === "date" ? "date" : "death",
      release_date: m.release_date ? utcIsoToWall(m.release_date, tz) : "",
      release_tz: tz });
    setView("edit");
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const data = await uploadBlob(file, file.name);
        if (data.kind === "video") {
          const poster = await captureVideoPoster(file);
          if (poster) { const p = await uploadBlob(poster, `poster-${Date.now()}.jpg`); data.poster = p.storage_path; }
        }
        setForm((f) => ({ ...f, media: [...f.media, data] }));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel caricamento");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const handleSong = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setUploadingSong(true);
    try { const data = await uploadBlob(file, file.name); setForm((f) => ({ ...f, song: data })); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel caricamento"); }
    finally { setUploadingSong(false); if (songRef.current) songRef.current.value = ""; }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Aggiungi un titolo");
    if (form.release_type === "date" && !form.release_date) return toast.error("Scegli la data di rilascio");
    setSaving(true);
    try {
      const tz = form.release_tz || BROWSER_TZ;
      const payload = {
        title: form.title, content: form.content, media: form.media, song: form.song,
        release_type: form.release_type,
        release_date: form.release_type === "date" ? wallToUtcIso(form.release_date, tz) : null,
        release_tz: form.release_type === "date" ? tz : null,
        event_description: null, recipients: [], heir_ids: [person.heir_id],
      };
      if (form.id) { await api.put(`/memories/${form.id}`, payload); toast.success("Messaggio salvato"); }
      else { await api.post("/memories", payload); toast.success("Messaggio dedicato creato"); }
      setView("list"); load(); onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const remove = async (m) => {
    if (!window.confirm("Eliminare questo messaggio dedicato?")) return;
    try { await api.delete(`/memories/${m.id}`); toast.success("Messaggio eliminato"); load(); onChanged && onChanged(); }
    catch { toast.error("Errore nell'eliminazione"); }
  };

  const relLabel = (m) => {
    if (m.release_type !== "date") return "Alla tua scomparsa";
    if (!m.release_date) return "In data —";
    const tz = m.release_tz || BROWSER_TZ;
    const d = new Date(m.release_date);
    const when = d.toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short", timeZone: tz });
    return `In data ${when} (${tzShortLabel(tz)})`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#F9F6F0] rounded-2xl max-w-lg max-h-[88vh] overflow-y-auto" data-testid="person-messages-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            {view === "edit" && (
              <button onClick={() => setView("list")} className="p-1 -ml-1 rounded-full hover:bg-[#F4F1EA]" data-testid="pm-back"><ArrowLeft className="w-5 h-5" /></button>
            )}
            {view === "list" ? `Messaggi per ${person?.name}` : (form.id ? "Modifica messaggio" : "Nuovo messaggio dedicato")}
          </DialogTitle>
          <DialogDescription className="sr-only">Gestisci i messaggi dedicati esclusivamente a questa persona e la loro modalità di rilascio.</DialogDescription>
        </DialogHeader>

        {view === "list" ? (
          <div className="space-y-3">
            {!person?.email && (
              <div className="flex items-start gap-2 text-xs text-[#8a5a2b] bg-[#F7EFE0] border border-[#e6d4b3] rounded-xl p-3" data-testid="pm-no-email">
                <Mail className="w-4 h-4 mt-0.5 shrink-0" /> Aggiungi un'email a questa persona per poterle recapitare il link quando il messaggio verrà rilasciato.
              </div>
            )}
            {items === null ? (
              <p className="text-muted-foreground text-sm py-6 text-center">Caricamento…</p>
            ) : items.length === 0 ? (
              <div className="text-center py-10" data-testid="pm-empty">
                <PenLine className="w-7 h-7 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">Nessun messaggio dedicato ancora.</p>
              </div>
            ) : (
              items.map((m) => (
                <div key={m.id} className="bg-white rounded-xl border border-border p-4 flex items-start gap-3" data-testid={`pm-item-${m.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-lg leading-tight truncate">{m.title}</p>
                    {m.content && <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{m.content}</p>}
                    <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full mt-2 ${m.release_type === "date" ? "bg-[#FBEDE7] text-primary" : "bg-[#F0EAF4] text-[#8A6D9C]"}`}>
                      {m.release_type === "date" ? <Calendar className="w-3.5 h-3.5" /> : <Heart className="w-3.5 h-3.5" />} {relLabel(m)}
                    </span>
                    {(m.media?.length > 0 || m.song) && <p className="text-xs text-muted-foreground mt-2">{(m.media?.length || 0)} allegati{m.song ? " + canzone" : ""}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => openEdit(m)} className="p-2 rounded-full hover:bg-[#F4F1EA]" data-testid={`pm-edit-${m.id}`}><PenLine className="w-4 h-4 text-muted-foreground" /></button>
                    <button onClick={() => remove(m)} className="p-2 rounded-full hover:bg-destructive/10" data-testid={`pm-delete-${m.id}`}><Trash2 className="w-4 h-4 text-destructive" /></button>
                  </div>
                </div>
              ))
            )}
            <Button onClick={openNew} className="w-full rounded-full bg-primary hover:bg-[#b0553d] text-white h-11" data-testid="pm-new-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuovo messaggio dedicato
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Titolo del messaggio…"
                className="font-serif text-xl border-0 border-b border-border rounded-none px-0 focus-visible:ring-0 focus-visible:border-primary bg-transparent" data-testid="pm-title-input" />
            </div>
            <div>
              <Label className="mb-1.5 block">Il tuo messaggio</Label>
              <Textarea value={form.content} onChange={(e) => set("content", e.target.value)} rows={5}
                placeholder="Scrivi qui parole dedicate solo a questa persona…" className="bg-white border-border rounded-xl resize-none" data-testid="pm-content-input" />
            </div>

            <div>
              <Label className="mb-2 block">Foto, video, vocali, documenti</Label>
              <div className="grid grid-cols-3 gap-2">
                {form.media.map((m) => (
                  <div key={m.storage_path} className="relative rounded-lg overflow-hidden border border-border bg-[#F4F1EA]">
                    {m.kind === "audio" ? <div className="p-2"><AuthMedia item={m} /></div> : <AuthMedia item={m} className="w-full h-20 object-cover" />}
                    <button onClick={() => set("media", form.media.filter((x) => x.storage_path !== m.storage_path))} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 shadow hover:bg-destructive hover:text-white" data-testid="pm-media-remove"><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <button onClick={() => fileRef.current?.click()} disabled={uploading} className="h-20 rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-primary hover:text-primary" data-testid="pm-media-add">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}<span className="text-[11px]">Aggiungi</span>
                </button>
              </div>
              <input ref={fileRef} type="file" accept="image/*,video/*,audio/*,application/pdf" multiple onChange={handleUpload} className="hidden" data-testid="pm-media-input" />
            </div>

            <div>
              <Label className="mb-1.5 block">Dedica musicale (facoltativa)</Label>
              {form.song ? (
                <div className="flex items-center gap-2 bg-[#F0EAF4] rounded-xl p-3">
                  <Music4 className="w-4 h-4 text-[#8A6D9C] shrink-0" />
                  <span className="text-sm flex-1 truncate">{form.song.original_filename}</span>
                  <button onClick={() => set("song", null)} className="text-destructive p-1" data-testid="pm-song-remove"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <button onClick={() => songRef.current?.click()} disabled={uploadingSong} className="inline-flex items-center gap-2 text-sm text-primary hover:underline" data-testid="pm-song-add">
                  {uploadingSong ? <Loader2 className="w-4 h-4 animate-spin" /> : <Music4 className="w-4 h-4" />} Abbina una canzone
                </button>
              )}
              <input ref={songRef} type="file" accept="audio/*" onChange={handleSong} className="hidden" data-testid="pm-song-input" />
            </div>

            <div>
              <Label className="mb-2 block">Quando rilasciare questo messaggio</Label>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => set("release_type", "death")} data-testid="pm-release-death"
                  className={`text-left p-3 rounded-xl border transition-colors ${form.release_type === "death" ? "border-primary bg-[#FBEDE7]" : "border-border bg-white hover:border-primary/40"}`}>
                  <div className="flex items-center gap-2"><Heart className={`w-4 h-4 ${form.release_type === "death" ? "text-primary" : "text-muted-foreground"}`} /><span className="text-sm font-medium">Alla mia scomparsa</span></div>
                </button>
                <button onClick={() => set("release_type", "date")} data-testid="pm-release-date"
                  className={`text-left p-3 rounded-xl border transition-colors ${form.release_type === "date" ? "border-primary bg-[#FBEDE7]" : "border-border bg-white hover:border-primary/40"}`}>
                  <div className="flex items-center gap-2"><Calendar className={`w-4 h-4 ${form.release_type === "date" ? "text-primary" : "text-muted-foreground"}`} /><span className="text-sm font-medium">In una data</span></div>
                </button>
              </div>
              {form.release_type === "date" && (
                <div className="mt-3 space-y-2">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex-1">
                      <Input type="datetime-local" value={form.release_date} onChange={(e) => set("release_date", e.target.value)} className="bg-white border-border rounded-xl h-11" data-testid="pm-release-date-input" />
                    </div>
                    <div className="sm:w-56">
                      <select
                        value={form.release_tz || BROWSER_TZ}
                        onChange={(e) => set("release_tz", e.target.value)}
                        data-testid="pm-release-tz-select"
                        className="w-full h-11 rounded-xl border border-border bg-white px-3 text-sm text-[#2C2A29] focus:outline-none focus:ring-2 focus:ring-primary/30"
                      >
                        {ALL_TIMEZONES.map((tz) => (
                          <option key={tz} value={tz}>
                            {tzShortLabel(tz)} — {tz} ({tzOffsetLabel(tz)})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-[#EEF3EC] text-[#4b6b47]" data-testid="pm-release-tz-hint">
                    <Clock className="w-3.5 h-3.5" />
                    Orario di recapito: {tzShortLabel(form.release_tz || BROWSER_TZ)} ({tzOffsetLabel(form.release_tz || BROWSER_TZ)})
                  </div>
                  <p className="text-xs text-muted-foreground">Alla data e ora scelte (nel fuso orario indicato), {person?.name} riceverà un'email con il link per aprire questo messaggio (anche se sei ancora in vita).</p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button onClick={save} disabled={saving} className="flex-1 rounded-full bg-primary hover:bg-[#b0553d] text-white h-11" data-testid="pm-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Salva
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PersonMessages;
